// ✅ src/pages/VehicleV2/VehicleSalesScrapListing.jsx  (UPDATED FULL FILE)
// Production listing design template
//
// Mount backend:
//   app.use("/api/salesandScrapListing", salesandScrapListing);
//
// Backend endpoints used:
//   GET  /api/salesandScrapListing/dropdowns?branchId=&branchType=&loginUserId=
//   GET  /api/salesandScrapListing/list?branchId=&branchType=&loginUserId=&salesScrap=&vehicleCode=&store=&vehicleName=&vehicleNumber=&vehicleMainType=
//   POST /api/salesandScrapListing/delete   { ids:[...] }

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaEye,
  FaArrowLeft,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/salesandScrapListing";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const clean = (v) => String(v ?? "").trim();

const selectValue = (v, def = "Select") => {
  const s = clean(v);
  return s ? s : def;
};

const defaultFilters = {
  salesScrap: "All",
  vehicleCode: "",
  store: "Select",
  vehicleName: "",
  vehicleNumber: "",
  vehicleMainType: "Select",
  search: "",
};

const defaultSaleTypes = [
  { label: "All", value: "All" },
  { label: "Sales", value: "Sales" },
  { label: "Scrap", value: "Scrap" },
];

const VehicleSalesScrapListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  /* ---------------- shared local/session pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [showFilters, setShowFilters] = useState(true);

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [filters, setFilters] = useState(defaultFilters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const [opts, setOpts] = useState({
    stores: [{ label: "Select", value: "Select" }],
    vehicleMainTypes: [{ label: "Select", value: "Select" }],
    saleTypes: defaultSaleTypes,
  });

  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const isBusy = pageLoading || dropdownLoading || gridLoading || actionLoading;

  /* ---------------- styles ---------------- */
  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetFiltersOnly = useCallback(() => {
    setFilters(defaultFilters);
    setPage(1);
    setSelectedIds([]);
  }, []);

  const buildListParams = useCallback(
    (override = {}) => {
      const f = filtersRef.current;
      return {
        branchId,
        branchType,
        loginUserId,
        salesScrap: selectValue(override.salesScrap ?? f.salesScrap, "All"),
        vehicleCode: toStr(override.vehicleCode ?? f.vehicleCode),
        vehicleName: toStr(override.vehicleName ?? f.vehicleName),
        vehicleNumber: toStr(override.vehicleNumber ?? f.vehicleNumber),
        vehicleMainType: selectValue(
          override.vehicleMainType ?? f.vehicleMainType,
          "Select"
        ),
        store: selectValue(override.store ?? f.store, "Select"),
      };
    },
    [branchId, branchType, loginUserId]
  );

  /* =========================================================
     mapped rows + quick search + pagination
     ========================================================= */
  const mappedRows = useMemo(() => {
    const q = toStr(filters.search).toLowerCase();

    const list = toList(rows).map((r, idx) => {
      const id =
        r.id ||
        r.vehicleId ||
        r.fld_id ||
        r.fld_vehicleid ||
        r.salesScrapId ||
        "";

      const branch =
        r.branch ||
        r.fld_BranchName ||
        r.fld_branchname ||
        r.branchName ||
        "";

      const vehicleMainType =
        r.vehicleMainType ||
        r.VehicleType ||
        r.fld_vehiclemaintype ||
        r.fld_vehicletypename ||
        r.vehicletype ||
        "";

      const vehicleCode =
        r.vehicleCode || r.fld_vehicle_code || r.fld_vehiclecode || "";

      const vehicleName =
        r.vehicleName || r.fld_vehicle_name || r.fld_vehiclename || "";

      const vehicleNumber =
        r.vehicleNumber ||
        r.fld_vehicle_regno ||
        r.fld_vehicleno ||
        r.vehicleNo ||
        "";

      const salesScrap =
        r.salesScrap || r.fld_salesscrap || r.saleType || r.type || "";

      return {
        __rowId: idx + 1,
        raw: r,
        id: toInt(id),
        branch,
        vehicleMainType,
        vehicleCode,
        vehicleName,
        vehicleNumber,
        salesScrap: toStr(salesScrap),
      };
    });

    if (!q) return list;

    return list.filter((x) => {
      return (
        toStr(x.branch).toLowerCase().includes(q) ||
        toStr(x.vehicleMainType).toLowerCase().includes(q) ||
        toStr(x.vehicleCode).toLowerCase().includes(q) ||
        toStr(x.vehicleName).toLowerCase().includes(q) ||
        toStr(x.vehicleNumber).toLowerCase().includes(q) ||
        toStr(x.salesScrap).toLowerCase().includes(q)
      );
    });
  }, [rows, filters.search]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(mappedRows.length / pageSize)),
    [mappedRows.length]
  );

  const paginatedRows = useMemo(
    () => mappedRows.slice((page - 1) * pageSize, page * pageSize),
    [mappedRows, page]
  );

  const recordsCount = useMemo(() => mappedRows.length, [mappedRows.length]);

  const currentPageIds = useMemo(
    () => paginatedRows.map((r) => toInt(r.id)).filter(Boolean),
    [paginatedRows]
  );

  const selectAll = useMemo(() => {
    if (currentPageIds.length === 0) return false;
    return currentPageIds.every((id) => selectedIds.includes(id));
  }, [currentPageIds, selectedIds]);

  const canDelete = selectedIds.length > 0 && !actionLoading;

  /* =========================================================
     API LOADERS
     ========================================================= */
  const loadDropdowns = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/dropdowns`, {
      params: {
        branchId,
        branchType,
        loginUserId,
      },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load dropdowns");
    }

    const stores = toList(data.stores).map((x) => ({
      label: String(x.label ?? x.name ?? "Select"),
      value: String(x.value ?? x.id ?? "Select"),
    }));

    const vehicleMainTypes = toList(
      data.vehicleMainTypes || data.vehicleTypes
    ).map((x) => ({
      label: String(x.label ?? x.name ?? x.fld_vehicletypename ?? "Select"),
      value: String(x.value ?? x.id ?? x.fld_vehicletypeindex ?? "Select"),
    }));

    const saleTypes = toList(data.saleTypes).map((x) => ({
      label: String(x.label ?? x.name ?? x.value ?? "All"),
      value: String(x.value ?? x.id ?? x.label ?? "All"),
    }));

    setOpts({
      stores: stores.length ? stores : [{ label: "Select", value: "Select" }],
      vehicleMainTypes: vehicleMainTypes.length
        ? vehicleMainTypes
        : [{ label: "Select", value: "Select" }],
      saleTypes: saleTypes.length ? saleTypes : defaultSaleTypes,
    });
  }, [branchId, branchType, loginUserId, commonHeaders]);

  const loadList = useCallback(
    async ({ silent = false, overrideFilters = null } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(overrideFilters || {}),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          setPage(1);
          setSelectedIds([]);
          if (!silent) toast.warn(data?.message || "No records found");
          return;
        }

        const listRows = toList(data.list);
        setRows(listRows);
        setPage(1);
        setSelectedIds([]);

        if (!silent) {
          toast.success(`Found ${listRows.length} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        setPage(1);
        setSelectedIds([]);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, commonHeaders, buildListParams, canUse]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);
      setDropdownLoading(true);
      await loadDropdowns();
      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setDropdownLoading(false);
      setPageLoading(false);
    }
  }, [loginUserId, branchId, canUse, loadDropdowns, loadList]);

  /* ---------------- actions ---------------- */
  const handleSearch = useCallback(async () => {
    await loadList({ silent: false });
  }, [loadList]);

  const handleViewAll = useCallback(async () => {
    const resetObj = { ...defaultFilters };
    setFilters(resetObj);

    await loadList({
      silent: false,
      overrideFilters: resetObj,
    });
  }, [loadList]);

  const handleRefresh = useCallback(async () => {
    await loadList({ silent: false });
  }, [loadList]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedIds((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );
      return;
    }

    setSelectedIds((prev) => {
      const merged = [...prev];
      currentPageIds.forEach((id) => {
        if (!merged.includes(id)) merged.push(id);
      });
      return merged;
    });
  }, [selectAll, currentPageIds]);

  const toggleSelect = useCallback((id) => {
    const n = toInt(id);
    if (!n) return;

    setSelectedIds((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]
    );
  }, []);

  const deleteSelected = useCallback(async () => {
    if (!selectedIds.length) {
      toast.warn("Please select at least one record!");
      return;
    }

    try {
      setActionLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        { ids: selectedIds },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(`Deleted ${data.deleted || selectedIds.length} record(s)`);
      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Delete failed"
      );
    } finally {
      setActionLoading(false);
    }
  }, [selectedIds, commonHeaders, loadList]);

  const openEdit = useCallback(
    (row) => {
      navigate("/vehicle/Vehicle_SalesandScrapEntry", {
        state: { id: row.id, row: row.raw || row },
      });
    },
    [navigate]
  );

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
    // Init once on mount. loadList no longer changes when filter text changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE SALES AND SCRAP LISTING
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Vehicle Sales and Scrap Listing
              </h2>
              <p
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {branchId || "-"} • User ID: {loginUserId || "-"} •
                Records: {recordsCount} • Selected: {selectedIds.length}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate("/vehicle/Vehicle_SalesandScrapList")}
                disabled={pageLoading}
                type="button"
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((s) => !s)}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={pageLoading}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={resetFiltersOnly}
                    disabled={pageLoading}
                    type="button"
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleViewAll}
                    disabled={pageLoading}
                    type="button"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={pageLoading}
                    type="button"
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {gridLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Sales Or Scrap</label>
                  <select
                    name="salesScrap"
                    value={filters.salesScrap}
                    onChange={(e) => setField("salesScrap", e.target.value)}
                    className={inputClass}
                    disabled={pageLoading}
                  >
                    {opts.saleTypes.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={filters.vehicleCode}
                    onChange={(e) => setField("vehicleCode", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                  />
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="store"
                    value={filters.store}
                    onChange={(e) => setField("store", e.target.value)}
                    className={inputClass}
                    disabled={pageLoading}
                  >
                    {opts.stores.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={filters.vehicleName}
                    onChange={(e) => setField("vehicleName", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    value={filters.vehicleNumber}
                    onChange={(e) => setField("vehicleNumber", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    className={inputClass}
                    placeholder="Enter vehicle number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainType"
                    value={filters.vehicleMainType}
                    onChange={(e) =>
                      setField("vehicleMainType", e.target.value)
                    }
                    className={inputClass}
                    disabled={pageLoading}
                  >
                    {opts.vehicleMainTypes.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Quick Search</label>
                  <input
                    value={filters.search}
                    onChange={(e) => setField("search", e.target.value)}
                    className={inputClass}
                    placeholder="Search in results..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {(pageLoading || dropdownLoading) && rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {pageLoading ? "Loading page..." : "Loading dropdowns..."}
                </div>
              </div>
            ) : mappedRows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {gridLoading ? "Searching..." : "No Records found"}
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        S.No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Branch
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Main Type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Number
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Sales/Scrap
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">
                        Edit
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          disabled={actionLoading || currentPageIds.length === 0}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    } divide-y`}
                  >
                    {paginatedRows.map((row, idx) => {
                      const sno = (page - 1) * pageSize + idx + 1;
                      const isSelected = selectedIds.includes(toInt(row.id));
                      const isSales =
                        toStr(row.salesScrap).toLowerCase() === "sales";

                      return (
                        <tr
                          key={`${row.id || row.__rowId || ""}-${sno}`}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {sno}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.branch || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleMainType || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-blue-400" : "text-blue-600"
                            }`}
                          >
                            {row.vehicleCode || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleName || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleNumber || "-"}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`font-semibold ${
                                isSales
                                  ? isDark
                                    ? "text-green-400"
                                    : "text-green-600"
                                  : isDark
                                  ? "text-red-400"
                                  : "text-red-600"
                              }`}
                            >
                              {row.salesScrap || "-"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              disabled={actionLoading}
                              className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                              title="Edit"
                            >
                              ✏️
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(row.id)}
                              disabled={actionLoading || !row.id}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="p-4">
                  <button
                    onClick={deleteSelected}
                    disabled={!canDelete}
                    type="button"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      canDelete ? "" : "opacity-50 cursor-not-allowed"
                    } bg-red-600 hover:bg-red-700 text-white`}
                  >
                    <FaTrash />
                    {actionLoading ? "Deleting..." : "Remove"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          {mappedRows.length > 0 && totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <nav className="flex gap-1 flex-wrap items-center">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  type="button"
                  className={`px-3 py-1 border rounded text-sm ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  } disabled:opacity-50`}
                >
                  Prev
                </button>

                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    type="button"
                    className={`px-3 py-1 border rounded text-sm ${
                      page === i + 1
                        ? `${buttonClass} text-white`
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  type="button"
                  className={`px-3 py-1 border rounded text-sm ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  } disabled:opacity-50`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSalesScrapListing;